import React, { useState, useEffect, useRef } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { ColorModeContext, useMode } from "./theme";
import HomePage from "./pages/HomePage";
import Generator from "./pages/Generator";
import LoginPage from "./pages/Login";
import { useDispatch, useSelector } from "react-redux";
import PersonalityAnalysis from "./pages/PersonalityAnalysis";
import Pricing from "./pages/Pricing";
import Plans from "./pages/Plans";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Subscription from "./pages/Account/Subscription";

import { Toaster } from "react-hot-toast";

import ErrorPage from "./pages/404ErrorPage";

import toastAlert from "./components/Items/ToastReact";

import "./App.css";

// _________________________________
import i18n from "i18next";
import LanguageContext from "./LanguageContext";

import { logoutUser, checkAuthSession, logOutUser, setSession } from "./app/features/auth";
import { resetLocation } from "./app/features/location";
import CustomLoader from "./components/Items/CustomLoader";
import VerifyAndUpdate from "./pages/SubAdmins/VerifyAndUpdate";
import { onAuthStateChange } from "./services/supabaseAuth";
// _________________________________

function App() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { session, user, isFetching } = useSelector((state) => state.auth);
  const [isLoading, setIsLoading] = useState(true);
  const hadSessionRef = useRef(false);
  const isInitializedRef = useRef(false);

  const [theme, colorMode] = useMode();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const handleLogOut = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      dispatch(resetLocation());
      navigate("/");
      return;
    } catch (error) {
      toastAlert("error", "Something went wrong");
    }
  };

  // Check authentication session on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    console.log("🔵 App.js: useEffect started");
    const initializeAuth = async () => {
      try {
        console.log("🔵 App.js: Setting isLoading to true");
        setIsLoading(true);
        console.log("🔵 App.js: Calling checkAuthSession...");
        const result = await dispatch(checkAuthSession()).unwrap();
        console.log("🔵 App.js: checkAuthSession completed:", result);
        hadSessionRef.current = !!result?.session;
        isInitializedRef.current = true;
      } catch (error) {
        console.error("❌ App.js: Error initializing auth:", error);
        isInitializedRef.current = true;
      } finally {
        console.log("🔵 App.js: Setting isLoading to false");
        setIsLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth state changes
    console.log("🔵 App.js: Setting up auth state listener");
    let isHandlingSignOut = false;
    const { data: { subscription } } = onAuthStateChange(async (event, session) => {
      console.log("🔵 App.js: Auth state changed:", event, session ? "session exists" : "no session");

      // Ignore INITIAL_SESSION - it's already handled by checkAuthSession on mount
      if (event === "INITIAL_SESSION") {
        console.log("🔵 App.js: INITIAL_SESSION event - ignoring (already handled)");
        hadSessionRef.current = !!session;
        return;
      }

      // After initialization, ignore events that don't represent actual state changes
      // This prevents reloads when window regains focus
      if (isInitializedRef.current && event !== "SIGNED_OUT") {
        // For SIGNED_IN and TOKEN_REFRESHED after init, only update if session actually exists
        // and we already had a session (meaning it's just a refresh, not a new login)
        if ((event === "SIGNED_IN" || event === "TOKEN_REFRESHED") && session && hadSessionRef.current) {
          console.log(`🔵 App.js: ${event} after init with existing session - silently updating`);
          dispatch(setSession({ session, user: session.user }));
          return;
        }
      }

      if (event === "SIGNED_IN" && session) {
        console.log("🔵 App.js: User signed in event");
        isHandlingSignOut = false;

        // Only do full check if we didn't have a session before
        // If we already had a session, this is likely just a focus/refresh event
        if (!hadSessionRef.current) {
          console.log("🔵 App.js: No previous session - doing full check");
          hadSessionRef.current = true;
          await dispatch(checkAuthSession()).unwrap();
        } else {
          console.log("🔵 App.js: Already had session - silently updating");
          // Just update the session silently without showing loader
          hadSessionRef.current = true;
          dispatch(setSession({ session, user: session.user }));
        }
      } else if (event === "SIGNED_OUT") {
        // Only handle SIGNED_OUT if we actually had a session before
        // This prevents handling SIGNED_OUT when user is already logged out
        if (hadSessionRef.current && !isHandlingSignOut) {
          console.log("🔵 App.js: User signed out (had session before)");
          isHandlingSignOut = true;
          hadSessionRef.current = false;
          // Use the reducer action directly instead of the async thunk to avoid triggering more events
          // The async thunk calls signOut() which can trigger more SIGNED_OUT events
          dispatch(logOutUser());
          dispatch(resetLocation());
          // Reset flag after a short delay to allow handling future legitimate sign outs
          setTimeout(() => {
            isHandlingSignOut = false;
          }, 1000);
        } else {
          console.log("🔵 App.js: SIGNED_OUT ignored (no previous session or already handling)");
          hadSessionRef.current = false;
        }
      } else if (event === "TOKEN_REFRESHED" && session) {
        // Fallback for TOKEN_REFRESHED that didn't get caught by early return
        // (shouldn't happen, but just in case)
        console.log("🔵 App.js: Token refreshed (fallback handler)");
        hadSessionRef.current = true;
        dispatch(setSession({ session, user: session.user }));
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [dispatch]);

  // Update ref when session changes (separate effect to avoid recreating subscription)
  useEffect(() => {
    hadSessionRef.current = !!session;
  }, [session]);

  console.log("🔵 App.js: Render - isLoading:", isLoading, "isFetching:", isFetching, "session:", session ? "exists" : "null");

  if (isLoading || isFetching) {
    console.log("🔵 App.js: Showing loader");
    return (
      <div
        style={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CustomLoader />
      </div>
    );
  }

  return (
    <>
      <LanguageContext.Provider value={changeLanguage}>
        <ColorModeContext.Provider value={colorMode}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/generator" element={<Generator />} />
              <Route path="/personality-analysis" element={<PersonalityAnalysis />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/plans" element={<Plans />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/subscription" element={<Subscription />} />
              <Route path="/verify-and-update-password" element={<VerifyAndUpdate />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="*" element={<ErrorPage />} />
            </Routes>
          </ThemeProvider>
        </ColorModeContext.Provider>
      </LanguageContext.Provider>

      {/* // )} */}
      <div>
        <Toaster position="top-center" />
      </div>
    </>
  );
}

export default App;
